import {
  EHeadingStyleVariant,
  EIconButtonVariant,
  ETextStyleVariant,
  GeneralCaretLeftIcon,
  GeneralCaretRightIcon,
  Heading,
  IconButton,
  Text,
} from '@outdoorsyco/bonfire';
import Image from 'next/image';
import React, { useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Slider, { Settings as SliderSettings } from 'react-slick';

import RVBookings from '@/assets/become-a-host/rv/bookings.png';
import RVBookingsBG from '@/assets/become-a-host/rv/bookings-bg.jpg';
import RVCalendar from '@/assets/become-a-host/rv/calendar.png';
import RVCalendarBG from '@/assets/become-a-host/rv/calendar-bg.jpg';
import RVDelivery from '@/assets/become-a-host/rv/delivery.png';
import RVDeliveryBG from '@/assets/become-a-host/rv/delivery-bg.jpg';
import RVEarnings from '@/assets/become-a-host/rv/earnings.png';
import RVEarningsBG from '@/assets/become-a-host/rv/earnings-bg.jpg';
import RVInsights from '@/assets/become-a-host/rv/insights.png';
import RVInsightsBG from '@/assets/become-a-host/rv/insights-bg.jpg';
import RVMessages from '@/assets/become-a-host/rv/messages.png';
import RVMessagesBG from '@/assets/become-a-host/rv/messages-bg.jpg';
import RVSmartMatch from '@/assets/become-a-host/rv/smart-match.png';
import RVSmartMatchBG from '@/assets/become-a-host/rv/smart-match-bg.jpg';
import RVSupport from '@/assets/become-a-host/rv/support.png';
import RVSupportBG from '@/assets/become-a-host/rv/support-bg.jpg';

import styles from './BecomeAHost.module.css';

export const HostingTools = () => {
  const settings: Partial<SliderSettings> = {
    dots: false,
    infinite: true,
    arrows: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  const sliderRef = useRef<Slider>(null);
  const intl = useIntl();

  const hostingToolImageBGAlt = intl.formatMessage({
    defaultMessage: 'Hosting tools background',
    id: 'Ti6b5J',
  });
  const hostingToolImageAlt = intl.formatMessage({ defaultMessage: 'Hosting tools', id: 'v1pXyw' });
  const toolList = [
    {
      title: intl.formatMessage({ defaultMessage: 'Calendar', id: 'Coy6SH' }),
      description: intl.formatMessage({
        defaultMessage:
          'Make hosting fit your lifestyle, not the other way around. Block off time for your own RV adventure or a little vehicle upkeep. Or, set aside some quiet time by marking days when trips can’t start or end.',
        id: 'TjLYac',
      }),
      imageBG: RVCalendarBG,
      image: RVCalendar,
      imageWidth: 375,
      imageHeight: 765,
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Messages', id: 'hMzcSq' }),
      description: intl.formatMessage({
        defaultMessage:
          'Hosting is all about communication. But, no need to overwork your fingers. Easily respond to guests and give them the information they need using prewritten quick replies. Type less and reply faster.',
        id: 'RbVxkG',
      }),
      imageBG: RVMessagesBG,
      image: RVMessages,
      imageWidth: 375,
      imageHeight: 647,
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Delivery', id: 'drqP2L' }),
      description: intl.formatMessage({
        defaultMessage:
          'Jump start your guest’s road trip by bringing the vehicle to them, at an airport or other locale. They love the convenience. Or, deliver and set up your RV at their final destination. They’ll never drive or tow it, meaning the captain’s chair is all yours.',
        id: 'LdyCzF',
      }),
      imageBG: RVDeliveryBG,
      image: RVDelivery,
      imageWidth: 375,
      imageHeight: 663,
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Smart Match', id: 'PpV/Cd' }),
      description: intl.formatMessage({
        defaultMessage:
          'Guests don’t always get the first vehicle they try to book. But, it’s not all bad news. We help out by matching them with other hosts in the area where they plan to travel. When you have a match, you can swoop in and save the day, offering to host them so their trip is still a go.',
        id: 'HgWRe2',
      }),
      imageBG: RVSmartMatchBG,
      image: RVSmartMatch,
      imageWidth: 375,
      imageHeight: 735,
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Earnings', id: '14sEVu' }),
      description: intl.formatMessage({
        defaultMessage:
          'We get it, compensation is important. So, you’ll be glad to know there’s more than one way to earn on every trip. It starts with your nightly rate, of course. But, you can also sell add-ons that enhance a guest’s experience. And, set pricing structures for things like mileage and generator usage.',
        id: 'RCN5I4',
      }),
      imageBG: RVEarningsBG,
      image: RVEarnings,
      imageWidth: 375,
      imageHeight: 587,
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Bookings', id: 'uxvKKF' }),
      description: intl.formatMessage({
        defaultMessage:
          'You’ve got options for how your vehicle gets booked. Go hands-free and let guests book your vehicle anytime it’s available — easy. Or, have guests send you their proposed trip. You can review it, ask questions, and then change, approve, or decline it.',
        id: 'os2atF',
      }),
      imageBG: RVBookingsBG,
      image: RVBookings,
      imageWidth: 375,
      imageHeight: 704,
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Insights', id: 'xK7rmd' }),
      description: intl.formatMessage({
        defaultMessage:
          'Easily see the rewards of hosting with at-a-glance insights on your earnings, bookings, and more. You might even discover you have a reason to brag. If not, don’t worry. We’ll offer some ideas on how to improve and get you some bragging rights in no time.',
        id: 'cDOXF5',
      }),
      imageBG: RVInsightsBG,
      image: RVInsights,
      imageWidth: 375,
      imageHeight: 734,
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Support', id: 'HqRNN8' }),
      description: intl.formatMessage({
        defaultMessage:
          'You have a partner in hosting — one with a crazy amount of experience and know-how. So, we hope you join us for webinars and other events designed to help you be the best host you can be. Our success managers look forward to hosting you.',
        id: 'Mv/kUD',
      }),
      imageBG: RVSupportBG,
      image: RVSupport,
      imageWidth: 375,
      imageHeight: 757,
    },
  ];

  return (
    <section className="mx-auto bg-white max-w-xxl">
      <Slider {...settings} ref={sliderRef}>
        {toolList.map(({ title, description, image, imageBG, imageHeight, imageWidth }, idx) => (
          <div key={`tool-${idx}`} className="relative">
            <div className="absolute top-0 right-0 w-full md:w-2/5 md:h-full h-[445px]">
              <Image
                src={imageBG}
                alt={`${title}-${hostingToolImageBGAlt}`}
                layout="fill"
                objectFit="cover"
                objectPosition="bottom"
                priority
              />
            </div>

            <div
              className={`flex flex-col-reverse items-center justify-between md:min-h-[600px] lg:min-h-[750px] xl:min-h-[800px] md:flex-row ${styles.spacing} !py-8`}>
              <div className="w-full md:w-2/5">
                <Text
                  variant={ETextStyleVariant.LegalRegular}
                  className="hidden uppercase md:block">
                  <FormattedMessage defaultMessage="OUTDOORSY HOSTING TOOLS" id="87XjST" />
                </Text>

                <div className="text-center md:text-left">
                  <Heading variant={EHeadingStyleVariant.H2} className="mt-6 mb-4">
                    {title}
                  </Heading>
                  <Text variant={ETextStyleVariant.LargeRegular}>{description}</Text>
                </div>

                <div className="flex justify-center mt-8 gap-3 md:justify-start">
                  <IconButton
                    icon={GeneralCaretLeftIcon}
                    onClick={sliderRef.current?.slickPrev}
                    size="small"
                    variant={EIconButtonVariant.Tertiary}
                  />
                  <IconButton
                    icon={GeneralCaretRightIcon}
                    onClick={sliderRef.current?.slickNext}
                    size="small"
                    variant={EIconButtonVariant.Tertiary}
                  />
                </div>
              </div>

              <div
                className={`relative flex justify-center w-auto mt-40 rounded-lg md:w-1/3 md:right-20 lg:right-32 md:mt-0 ${
                  idx + 1 !== toolList.length ? 'shadow-500' : ''
                }`}>
                <Image
                  src={image}
                  alt={`${title}-${hostingToolImageAlt}`}
                  width={imageWidth}
                  height={imageHeight}
                  className="rounded-lg"
                  priority
                />
              </div>
            </div>
          </div>
        ))}
      </Slider>
    </section>
  );
};
